import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SlotMachineHandle, SlotMachineProps } from './types';
import { useSlotMachineEngine } from './useSlotMachine';

// ---- Default icons (inline SVG) ----
const ShuffleIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: 6, verticalAlign: 'middle' }}
  >
    <polyline points="1 4 1 10 7 10" />
    <polyline points="23 20 23 14 17 14" />
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" />
  </svg>
);

const StopIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    style={{ marginRight: 6, verticalAlign: 'middle' }}
  >
    <rect x="4" y="4" width="16" height="16" rx="2" />
  </svg>
);

// ---- CSS blur class map ----
const blurClass: Record<string, string> = {
  fast: 'rsm-tile--blur-fast',
  normal: 'rsm-tile--blur-medium',
  slow: 'rsm-tile--blur-slow',
  turtle: 'rsm-tile--blur-turtle',
};

/** Number of full copies of displayItems in the tile strip for seamless wraparound. */
const TILE_COPIES = 3;

/**
 * React SlotMachine component — a 1:1 port of jQuery-SlotMachine.
 *
 * @example
 * ```tsx
 * <SlotMachine
 *   items={['Apple', 'Banana', 'Cherry', 'Date']}
 *   options={{ active: 0, delay: 300, direction: 'up', easing: true, easeDuration: 600 }}
 *   onStop={({ item, index }) => console.log('Winner:', item)}
 * />
 * ```
 */
export const SlotMachine = forwardRef<SlotMachineHandle, SlotMachineProps>(
  function SlotMachine(
    {
      items,
      itemKeys,
      initialItem,
      options,
      onStop,
      onShuffle,
      className,
      style,
      slotHeight = '100px',
      slotWidth = '100%',
      borderRadius = '12px',
      slotBackground = '#ffffff',
      slotBoxShadow,
      enableGradientMask = true,
      enableBlur = true,
      shuffleLabel = 'Shuffle!',
      stopLabel = 'Stop!',
      hideButtons = false,
      shuffleButtonClassName,
      stopButtonClassName,
      buttonsClassName,
      shuffleIcon,
      stopIcon,
      tileClassName,
    },
    ref,
  ) {
    // ---- initialItem support ----
    const hasInitialItem = initialItem !== undefined && initialItem !== null;

    // The items displayed in the slot (initialItem prepended if provided)
    const displayItems = useMemo<React.ReactNode[]>(() => {
      if (!hasInitialItem) return items;
      return [initialItem, ...items];
    }, [hasInitialItem, initialItem, items]);

    const logicalCount = displayItems.length;

    // ---- Numeric slot height ----
    const slotHeightNum = useMemo(() => parseFloat(slotHeight), [slotHeight]);

    // ---- Wrap engine options so randomize never picks index 0 when initialItem is set ----
    const engineOptions = useMemo(() => {
      const base = {
        ...options,
        active: options?.active ?? 0,
      };
      if (!hasInitialItem) return base;
      const userRandomize = options?.randomize;
      return {
        ...base,
        randomize: userRandomize
          ? (active: number, total: number) => {
              const result = userRandomize(active, total);
              return result <= 0 ? 1 : result;
            }
          : (_active: number, total: number) =>
              Math.floor(Math.random() * (total - 1)) + 1,
      };
    }, [hasInitialItem, options]);

    const {
      active: engineActive,
      running,
      stopping,
      speedTier,
      callbacks,
      shuffle,
      stop,
      next,
      prev,
    } = useSlotMachineEngine(logicalCount, engineOptions);

    // ---- Visual active (extended range for seamless wraparound) ----
    const direction = options?.direction ?? 'up';
    const prevEngineActiveRef = useRef(engineActive);

    // visualActive lives in [logicalCount, 2*logicalCount-1] (middle copy)
    const [visualActive, setVisualActive] = useState<number>(() => {
      const start = engineOptions.active ?? 0;
      return logicalCount > 0 ? logicalCount + start : 0;
    });

    const visualActiveRef = useRef(visualActive);
    visualActiveRef.current = visualActive;

    // Skip-transition flag — set when we wrap visualActive to stay within the tile list
    const [skipTransition, setSkipTransition] = useState(false);

    // Reset skipTransition after the wrapped frame commits
    useEffect(() => {
      if (!skipTransition) return;
      const raf = requestAnimationFrame(() => setSkipTransition(false));
      return () => cancelAnimationFrame(raf);
    }, [skipTransition]);

    // Determine if render is needed (skip if 0 items)
    const hasItems = logicalCount > 0;

    // When engine active changes, extend visualActive past the copy boundary if wrapping
    useEffect(() => {
      if (!hasItems) return;
      const prev = prevEngineActiveRef.current;
      const next = engineActive;
      prevEngineActiveRef.current = next;

      if (prev === next) return;

      const totalTiles = TILE_COPIES * logicalCount;
      let visual = visualActiveRef.current;
      let didWrap = false;

      if (direction === 'up') {
        // Always move FORWARD in visual space
        const delta = (next - prev + logicalCount) % logicalCount;
        const step = delta === 0 ? logicalCount : delta;
        visual += step;
      } else {
        // Always move BACKWARD in visual space
        const delta = (prev - next + logicalCount) % logicalCount;
        const step = delta === 0 ? logicalCount : delta;
        visual -= step;
      }

      // Wrap to stay within tile list bounds (visual jump is invisible —
      // tile content repeats every logicalCount)
      if (visual >= totalTiles) {
        visual -= logicalCount;
        didWrap = true;
      } else if (visual < 0) {
        visual += logicalCount;
        didWrap = true;
      }

      if (didWrap) {
        setSkipTransition(true);
      }

      visualActiveRef.current = visual;
      setVisualActive(visual);
    }, [engineActive, direction, logicalCount, hasItems]);

    // After the machine stops, snap visualActive back to the middle copy
    const wasRunningRef = useRef(running || stopping);
    useEffect(() => {
      const wasActive = wasRunningRef.current;
      wasRunningRef.current = running || stopping;

      // Only snap when transitioning from active → idle
      if (wasActive && !running && !stopping && hasItems) {
        // Snap to the middle copy (logicalCount .. 2*logicalCount-1)
        const snapped =
          ((visualActiveRef.current % logicalCount) + logicalCount) % logicalCount +
          logicalCount;
        if (snapped !== visualActiveRef.current) {
          visualActiveRef.current = snapped;
          setVisualActive(snapped);
        }
      }
    }, [running, stopping, logicalCount, hasItems]);

    // ---- Tile list: TILE_COPIES full copies of displayItems ----
    const tileList = useMemo((): React.ReactNode[] => {
      if (!hasItems) return [];
      const result: React.ReactNode[] = [];
      for (let copy = 0; copy < TILE_COPIES; copy++) {
        for (let i = 0; i < logicalCount; i++) {
          result.push(displayItems[i]);
        }
      }
      return result;
    }, [displayItems, logicalCount, hasItems]);

    // ---- TranslateY based on visualActive ----
    const translateY = useMemo(() => {
      if (!hasItems) return 0;
      return -(visualActive) * slotHeightNum;
    }, [visualActive, slotHeightNum, hasItems]);

    // ---- Dynamic transition style ----
    const tilesStyle: React.CSSProperties = useMemo(() => {
      // Wrap-frame: instant jump (no visible change — tile content repeats)
      if (skipTransition) {
        return {
          transform: `translateY(${translateY}px)`,
          transition: 'none',
        };
      }

      const baseDelay = options?.delay ?? 200;
      let duration: number;
      let easing: string;

      if (running || stopping) {
        switch (speedTier) {
          case 'fast':
            duration = baseDelay / 1.5;
            easing = 'linear';
            break;
          case 'normal':
            duration = baseDelay;
            easing = 'ease-out';
            break;
          case 'slow':
            duration = baseDelay * 1.5;
            easing = 'ease-out';
            break;
          case 'turtle':
            duration = baseDelay * 3;
            easing = 'ease-out';
            break;
          default:
            duration = baseDelay;
            easing = 'ease-out';
        }
      } else {
        // Idle snap-back: instant (no visible transition since we snap to same visual)
        duration = 0;
        easing = 'linear';
      }

      return {
        transform: `translateY(${translateY}px)`,
        transition: duration > 0
          ? `transform ${duration / 1000}s ${easing}`
          : 'none',
      };
    }, [translateY, speedTier, running, stopping, options?.delay, skipTransition]);

    // Wire callbacks via the mutable ref
    useEffect(() => {
      callbacks.current.onShuffle = onShuffle;
    }, [onShuffle, callbacks]);
    useEffect(() => {
      callbacks.current.onStop = (winner) => {
        // initialItem (index 0) is never a winner — skip silently
        if (hasInitialItem && winner.index <= 0) return;
        const itemIndex = hasInitialItem ? winner.index - 1 : winner.index;
        onStop?.({
          item: items[itemIndex],
          index: itemIndex,
          key: itemKeys?.[itemIndex],
        });
      };
    }, [onStop, items, itemKeys, callbacks, hasInitialItem]);

    // Expose imperative handle
    const handle: SlotMachineHandle = useMemo(
      () => ({
        shuffle,
        stop,
        next,
        prev,
        get active() {
          return engineActive;
        },
        get running() {
          return running;
        },
        get stopping() {
          return stopping;
        },
      }),
      [shuffle, stop, next, prev, engineActive, running, stopping],
    );
    useImperativeHandle(ref, () => handle, [handle]);

    // Blur state — derive from speedTier
    const blur = enableBlur ? (blurClass[speedTier] ?? '') : '';

    const wrapperBoxShadow =
      slotBoxShadow ?? '0 4px 24px rgba(0,0,0,0.12)';

    // Only render tiles if there are items
    if (logicalCount === 0) {
      return (
        <div
          className={`rsm-wrapper ${className ?? ''}`}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            ...style,
          }}
        >
          <div
            className="rsm-window"
            style={{
              width: slotWidth,
              height: slotHeight,
              borderRadius,
              background: slotBackground,
              boxShadow: wrapperBoxShadow,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: '0.9rem',
            }}
          >
            No items
          </div>
        </div>
      );
    }

    return (
      <div
        className={`rsm-wrapper ${className ?? ''}`}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          ...style,
        }}
      >
        {/* Slot window */}
        <div
          className={`rsm-window ${enableGradientMask ? 'rsm-window--gradient' : ''}`}
          style={{
            width: slotWidth,
            height: slotHeight,
            borderRadius,
            background: slotBackground,
            boxShadow: wrapperBoxShadow,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div className="rsm-tiles" style={tilesStyle}>
            {tileList.map((item, i) => (
              <div
                key={i}
                className={`rsm-tile ${tileClassName ?? ''} ${blur}`}
                style={{ height: slotHeight, flexShrink: 0 }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        {!hideButtons && (
          <div
            className={`rsm-buttons ${buttonsClassName ?? ''}`}
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
            }}
          >
            {!running ? (
              <button
                type="button"
                className={`rsm-btn rsm-btn--shuffle ${shuffleButtonClassName ?? ''}`}
                onClick={() => shuffle(999999)}
                disabled={items.length === 0}
              >
                {shuffleIcon ?? <ShuffleIcon />}
                <span>{shuffleLabel}</span>
              </button>
            ) : (
              <button
                type="button"
                className={`rsm-btn rsm-btn--stop ${stopButtonClassName ?? ''}`}
                onClick={() => stop(0)}
              >
                {stopIcon ?? <StopIcon />}
                <span>{stopLabel}</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default SlotMachine;
